import { type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useUiStore } from "@/stores/ui";
import { useAppState, useSession, useLogout } from "@/lib/queries";
import { InstallPromptButton } from "@/components/InstallPromptButton";
import { OfflineQueueBadge } from "@/components/OfflineQueueBadge";
import { AccountSwitcher } from "@/components/AccountSwitcher";
import {
  DropdownRoot,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
} from "@/components/ui/DropdownMenu";

interface TopBarProps {
  onMenuClick: () => void;
  brand?: ReactNode;
}

interface AppShape {
  uiAccess?: {
    isPlatformMember?: boolean;
    canViewSystemConsole?: boolean;
  };
}

export function TopBar({ onMenuClick, brand }: TopBarProps) {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const { data: session } = useSession();
  const { data: appRaw } = useAppState();
  const logout = useLogout();
  const navigate = useNavigate();

  const app = (appRaw ?? {}) as AppShape;
  const canSeeSystem = Boolean(app.uiAccess?.canViewSystemConsole);

  const userEmail =
    session && typeof session === "object" && "email" in session
      ? String((session as Record<string, unknown>).email)
      : "";

  const userInitial = userEmail
    ? userEmail.trim().charAt(0).toUpperCase()
    : "?";

  return (
    <header className="flex items-center h-14 px-4 bg-dw-bg-deep border-b border-dw-border shrink-0">
      {/* Mobile hamburger */}
      <button
        type="button"
        className="mr-3 p-1 text-dw-text-2 hover:text-dw-text md:hidden"
        onClick={onMenuClick}
        aria-label="Toggle menu"
      >
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Brand — defaults to tenant wordmark; SystemShell overrides. */}
      {brand ?? (
        <Link
          to={canSeeSystem ? "/system/overview" : "/operations"}
          aria-label="Recovery Console — go to home"
          className="font-display text-lg tracking-wider uppercase whitespace-nowrap select-none hover:opacity-80 transition-opacity"
        >
          <span className="text-dw-text">DORSEY</span>
          <span className="text-dw-blue mx-1">|</span>
          <span className="text-dw-text">WORX</span>
        </Link>
      )}

      <div className="ml-auto flex items-center gap-3">
        <AccountSwitcher />
        <OfflineQueueBadge />
        <InstallPromptButton />

        {/* Theme toggle */}
        <button
          type="button"
          className="p-1.5 rounded-dw-sm text-dw-text-2 hover:text-dw-text hover:bg-dw-surface-h transition-colors"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
        >
          {theme === "dark" ? (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 7.66l-.71-.71M4.05 4.05l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          ) : (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z"
              />
            </svg>
          )}
        </button>

        {/* User menu */}
        <DropdownRoot>
          <DropdownTrigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className="flex items-center gap-2 px-2 py-1 rounded-dw-sm hover:bg-dw-surface-h transition-colors"
            >
              <span
                aria-hidden="true"
                className="w-7 h-7 rounded-full bg-dw-blue text-dw-text-inv flex items-center justify-center text-xs font-semibold"
              >
                {userInitial}
              </span>
              {userEmail && (
                <span
                  className="text-xs text-dw-text-2 hidden sm:inline truncate max-w-[160px]"
                  title={userEmail}
                >
                  {userEmail}
                </span>
              )}
            </button>
          </DropdownTrigger>
          <DropdownContent align="end">
            {userEmail && (
              <>
                <div className="px-dw-3 py-dw-2 text-xs text-dw-text-3 truncate">
                  Signed in as{" "}
                  <span className="text-dw-text font-medium">{userEmail}</span>
                </div>
                <DropdownSeparator />
              </>
            )}
            {canSeeSystem ? (
              <>
                <DropdownItem onSelect={() => navigate("/system/overview")}>
                  System overview
                </DropdownItem>
                <DropdownItem onSelect={() => navigate("/system/settings")}>
                  My profile &amp; security
                </DropdownItem>
                <DropdownItem onSelect={() => navigate("/system/members")}>
                  Platform members
                </DropdownItem>
              </>
            ) : (
              <>
                <DropdownItem onSelect={() => navigate("/platform/setup")}>
                  Company setup
                </DropdownItem>
                <DropdownItem onSelect={() => navigate("/platform/admin")}>
                  My profile &amp; security
                </DropdownItem>
                <DropdownItem onSelect={() => navigate("/platform/members")}>
                  Team members
                </DropdownItem>
              </>
            )}
            <DropdownSeparator />
            <DropdownItem
              onSelect={() => {
                if (logout.isPending) return;
                logout.mutate();
              }}
              disabled={logout.isPending}
            >
              {logout.isPending ? "Signing out…" : "Sign out"}
            </DropdownItem>
          </DropdownContent>
        </DropdownRoot>
      </div>
    </header>
  );
}
